import { useTranslate } from "@similarweb/i18n-provider";
import { Connectga2Blue } from "@similarweb/icons/illustrations";
import { colorsPalettes } from "@similarweb/styles";
import { Button, IconButton } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { useCopyToClipboard } from "app/pages/sales-intelligence/hooks/useCopyToClipboard";
import {
    StyledMCPBannerCopyURLWrapper,
    StyledMCPBannerImgWrapper,
    StyledMCPConnectionBanner,
} from "app/pages/sales-intelligence/modules/manage-integrations/components/MCPConnectionBanner/styles";
import {
    MCP_CONNECTION_GUID_URL,
    MCP_CONNECTION_URL,
} from "app/pages/sales-intelligence/modules/manage-integrations/constants/common";
import manageIntegrationsTrackingService from "app/pages/sales-intelligence/modules/manage-integrations/services/tracking";
import {
    MCP_CONNECTION_BANNER_COPIED_BUTTON,
    MCP_CONNECTION_BANNER_COPY_BUTTON,
    MCP_CONNECTION_BANNER_GUID_BUTTON,
    MCP_CONNECTION_BANNER_TEXT,
    MCP_CONNECTION_BANNER_TITLE,
} from "app/pages/sales-intelligence/modules/manage-integrations/constants/translations";

const COPY_FEEDBACK_DURATION_MS = 3000;

interface IProps {
    isTrialButton: boolean;
    onUpgrade: VoidFunction;
}

const MCPConnectionBanner = ({ isTrialButton, onUpgrade }: IProps): React.ReactElement => {
    const { translate } = useTranslate();

    const { isCopied, onCopy } = useCopyToClipboard({ duration: COPY_FEEDBACK_DURATION_MS });

    const urlElement = (
        <StyledMCPBannerCopyURLWrapper>
            <Text
                fontVariant="input2"
                color={colorsPalettes.carbon["400"]}
            >
                {MCP_CONNECTION_URL}
            </Text>

            <IconButton
                type="primary"
                iconName="copy"
                onClick={() => {
                    manageIntegrationsTrackingService.trackMcpUrlCopy();
                    void onCopy(MCP_CONNECTION_URL);
                }}
            >
                {translate(isCopied ? MCP_CONNECTION_BANNER_COPIED_BUTTON : MCP_CONNECTION_BANNER_COPY_BUTTON)}
            </IconButton>
        </StyledMCPBannerCopyURLWrapper>
    );

    return (
        <StyledMCPConnectionBanner>
            <StyledMCPBannerImgWrapper>
                <Connectga2Blue size={300} />
            </StyledMCPBannerImgWrapper>

            <Stack gap="32px">
                <Stack gap="8px">
                    <Text
                        fontVariant="boldH4"
                        color={colorsPalettes.carbon["500"]}
                    >
                        {translate(MCP_CONNECTION_BANNER_TITLE)}
                    </Text>
                    <Text
                        fontVariant="body1"
                        color={colorsPalettes.carbon["300"]}
                    >
                        {translate(MCP_CONNECTION_BANNER_TEXT)}
                    </Text>
                </Stack>

                <Flex
                    gap="8px"
                    alignItems="center"
                >
                    {isTrialButton ? (
                        <IconButton
                            type="trial"
                            iconName="sparkles-gray"
                            iconSize="xs"
                            onClick={onUpgrade}
                        >
                            {translate("si.common.contact_us")}
                        </IconButton>
                    ) : (
                        urlElement
                    )}

                    <Button
                        type="flat"
                        onClick={() => window.open(MCP_CONNECTION_GUID_URL, "_blank", "noopener,noreferrer")}
                    >
                        {translate(MCP_CONNECTION_BANNER_GUID_BUTTON)}
                    </Button>
                </Flex>
            </Stack>
        </StyledMCPConnectionBanner>
    );
};

export default MCPConnectionBanner;
